/**
 * 指挥页实时轨迹画线 / 坐标处理（从 Dashboard 拆出）
 */
import { TRACK_LINE_PAINT } from './constants';
import { resolveTrackColor } from './trackColorHelpers';
import { validateCoordinates } from './trackHelpers';
import { toMapLngLat } from './basemapCoord';
import { splitTrackSegments, isSignificantTrackMove } from './trackSegmentHelpers';
import { upsertTrackGapLayer, removeTrackGapLayer } from './trackGapLayers';
import { getTrackLineWidth, getTrackGapDashed } from './trackVisualPrefs';

export function safeGetLayer(map, layerId) {
  try {
    return map?.getLayer?.(layerId) || null;
  } catch (_) {
    return null;
  }
}

export function createTrackSessionInfo(memberName) {
  const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  return {
    sessionId,
    memberName,
    startTime: new Date().toISOString(),
    status: 'active'
  };
}

export function buildNormalizedTrackPoint(member) {
  if (!member?.last_latitude || !member?.last_longitude) return null;
  const lat = parseFloat(parseFloat(member.last_latitude).toFixed(6));
  const lng = parseFloat(parseFloat(member.last_longitude).toFixed(6));
  if (!validateCoordinates(lat, lng)) return null;
  return {
    lat,
    lng,
    timestamp: member.last_seen || new Date().toISOString(),
    accuracy: Number.isFinite(Number(member.last_accuracy)) ? Number(member.last_accuracy) : null
  };
}

/** 位置是否离开精度圈（室内抖动不再记成轨迹） */
export function hasMovedSignificantly(lastPoint, newPoint) {
  if (!lastPoint) return true;
  return isSignificantTrackMove(lastPoint, newPoint);
}

function trackPointToHistoryShape(point) {
  return {
    lng: point?.lng,
    lat: point?.lat,
    longitude: point?.lng ?? point?.longitude,
    latitude: point?.lat ?? point?.latitude,
    accuracy: point?.accuracy ?? point?.acc,
    timestamp: point?.timestamp
  };
}

function displayTrackCoord(point, map) {
  const lng = Number(point?.lng ?? point?.longitude);
  const lat = Number(point?.lat ?? point?.latitude);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  if (lng === 0 && lat === 0) return null;
  const coord = toMapLngLat(lng, lat, undefined, map) || [lng, lat];
  if (!coord || Number.isNaN(coord[0]) || Number.isNaN(coord[1])) return null;
  return coord;
}

/** 实时轨迹按段输出（去掉往返尖刺，瞬移不连线） */
export function prepareTrackLineSegments(trackData = [], map = null) {
  if (!trackData || trackData.length === 0) return [];
  if (trackData.length === 1) {
    const coord = displayTrackCoord(trackData[0], map);
    if (!coord) return [];
    return [[[coord[0], coord[1]], [coord[0] + 0.0001, coord[1]]]];
  }
  const segs = splitTrackSegments(trackData.map(trackPointToHistoryShape));
  return segs
    .map((seg) => (seg || []).map((p) => displayTrackCoord(p, map)).filter(Boolean))
    .filter((coords) => coords.length >= 2);
}

export function prepareTrackLineCoordinates(trackData = [], map = null) {
  const segs = prepareTrackLineSegments(trackData, map);
  if (!segs.length) return [];
  return segs[0];
}

export function getRealtimeTrackLineId(memberName) {
  return `real-time-track-${memberName}`;
}

/**
 * 在地图上绘制/更新实时轨迹线
 * @returns {string|null} lineId
 */
export function drawRealtimeTrackLineOnMap(map, memberName, trackData, options = {}) {
  if (!map || !map.isStyleLoaded?.()) {
    return null;
  }

  const segments = prepareTrackLineSegments(trackData, map);
  if (!segments.length) {
    console.log(`⚠️ 队员 ${memberName} 坐标点不足，无法绘制轨迹线条`);
    return null;
  }

  const lineColor =
    options.lineColor ||
    resolveTrackColor(memberName, options.trackColors || {}, {
      name: memberName,
      deviceUniqueId: options.deviceUniqueId || options.device_unique_id,
      members: options.members
    });

  const lineId = getRealtimeTrackLineId(memberName);
  const geojsonData = {
    type: 'Feature',
    properties: {},
    geometry: segments.length === 1
      ? { type: 'LineString', coordinates: segments[0] }
      : { type: 'MultiLineString', coordinates: segments }
  };

  try {
    if (map.getSource(lineId)) {
      map.getSource(lineId).setData(geojsonData);
      if (map.getLayer(lineId)) {
        try { map.setPaintProperty(lineId, 'line-color', lineColor); } catch (_) { /* ignore */ }
        try { map.setPaintProperty(lineId, 'line-width', getTrackLineWidth()); } catch (_) { /* ignore */ }
      }
      console.log(`更新队员 ${memberName} 的轨迹线条，段数: ${segments.length}`);
      getTrackGapDashed() && upsertTrackGapLayer(map, lineId, trackData.map(trackPointToHistoryShape), {
        lineColor,
        lineWidth: getTrackLineWidth()
      });
      return lineId;
    }

    map.addSource(lineId, {
      type: 'geojson',
      data: geojsonData
    });

    let beforeId = null;
    try {
      if (map.getLayer('member-markers')) beforeId = 'member-markers';
    } catch (_) {
      /* ignore */
    }

    map.addLayer(
      {
        id: lineId,
        type: 'line',
        source: lineId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          ...TRACK_LINE_PAINT,
          'line-color': lineColor,
          'line-opacity': 0.8
        }
      },
      beforeId
    );

    // 加宽透明命中层，方便点击细轨迹线
    const hitId = `${lineId}-hit`;
    if (!map.getLayer(hitId)) {
      map.addLayer(
        {
          id: hitId,
          type: 'line',
          source: lineId,
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': '#000000',
            'line-width': 14,
            'line-opacity': 0
          }
        },
        lineId
      );
    }

    if (typeof options.onLineCreated === 'function') {
      options.onLineCreated(lineId);
    }

    getTrackGapDashed() && upsertTrackGapLayer(map, lineId, trackData.map(trackPointToHistoryShape), {
        lineColor,
        lineWidth: getTrackLineWidth()
      });
    console.log(`为队员 ${memberName} 创建新的轨迹线条，ID: ${lineId}`);
    return lineId;
  } catch (error) {
    console.error('绘制轨迹线条失败:', error);
    return null;
  }
}

export function removeRealtimeTrackLineFromMap(map, memberName, lineId) {
  if (!map) return;
  const id = lineId || getRealtimeTrackLineId(memberName);
  try {
    removeTrackGapLayer(map, id);
    const hitId = `${id}-hit`;
    if (map.getLayer(hitId)) map.removeLayer(hitId);
    if (map.getLayer(id)) map.removeLayer(id);
    if (map.getSource(id)) map.removeSource(id);
  } catch (error) {
    console.log(`移除轨迹线失败 ${id}:`, error);
  }
}

export function clampTrackRefreshIntervalMs(refreshIntervalSecOrMs) {
  // Dashboard 里 refreshInterval 多为秒；兼容已是毫秒的值
  const raw = Number(refreshIntervalSecOrMs) || 30;
  const ms = raw > 1000 ? raw : raw * 1000;
  return Math.max(5000, Math.min(ms, 60000));
}
